import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function App() {
  const [movies, setMovies] = useState([]);
  const [selectedMovie, setSelectedMovie] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch movie list on component mount
  useEffect(() => {
    fetchMovies();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchMovies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/movies`);
      setMovies(response.data.movies);
    } catch (err) {
      setError('Failed to load movies. Please check if backend is running.');
      console.error('Error fetching movies:', err);
    }
  };

  const getRecommendations = async () => {
    if (!selectedMovie) {
      setError('Please select a movie first!');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_BASE_URL}/recommend`, {
        movie_name: selectedMovie
      });
      
      setRecommendations(response.data.recommendations);
      setLoading(false);
    } catch (err) {
      setError('Failed to get recommendations. Please try again.');
      setLoading(false);
      console.error('Error getting recommendations:', err);
    }
  };

  // Filter movies based on search term
  const filteredMovies = movies.filter(movie =>
    movie.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle movie selection
  const handleSelectMovie = (movie) => {
    setSelectedMovie(movie);
    setSearchTerm(movie);
    setShowDropdown(false);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedMovie(''); // Clear selection when typing
    setShowDropdown(true);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && filteredMovies.length > 0) {
      handleSelectMovie(filteredMovies[0]);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <span className="text-4xl sm:text-5xl">🎬</span>
              <div className="text-center sm:text-left">
                <h1 className="text-2xl sm:text-3xl font-bold text-white">Movie Recommender</h1>
                <p className="text-purple-200 text-xs sm:text-sm">Discover your next favorite movie</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <a 
                href="https://github.com/Aech0" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-purple-200 transition-colors"
                aria-label="GitHub"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
              </a>
              <a 
                href="https://linkedin.com/in/vansh-narwani-464b9b217" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-purple-200 transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 sm:py-12">
        {/* Selection Section */}
        <div className="max-w-4xl mx-auto mb-8 sm:mb-12">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">
              🎯 Find Your Next Favorite Movie
            </h2>
            
            {/* Combined Search and Dropdown */}
            <div className="mb-6 relative" ref={dropdownRef}>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                Search & Select a Movie
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Start typing or click arrow to see all movies..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setShowDropdown(true)}
                  onKeyDown={handleKeyDown}
                  className="w-full px-4 py-2 sm:py-3 pr-12 text-sm sm:text-base border-2 border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 transition-colors"
                />
                
                {/* Dropdown Arrow Button */}
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 hover:bg-purple-50 rounded-lg transition-colors"
                  type="button"
                >
                  <svg 
                    className={`w-5 h-5 text-purple-600 transition-transform ${showDropdown ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown List */}
                {showDropdown && (
                  <div className="absolute z-10 w-full mt-2 bg-white border-2 border-purple-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                    {filteredMovies.length > 0 ? (
                      filteredMovies.slice(0, 100).map((movie, index) => (
                        <button
                          key={index}
                          onClick={() => handleSelectMovie(movie)}
                          className="w-full text-left px-4 py-2 hover:bg-purple-50 transition-colors text-sm sm:text-base border-b border-gray-100 last:border-b-0"
                        >
                          {movie}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-gray-500 text-sm">
                        No movies found
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Show results count */}
              {searchTerm && !selectedMovie && (
                <p className="text-xs text-gray-500 mt-1">
                  {filteredMovies.length} movie{filteredMovies.length !== 1 ? 's' : ''} found
                </p>
              )}
            </div>

            {/* Selected Movie Display */}
            {selectedMovie && (
              <div className="mb-6 p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Selected Movie:</p>
                    <p className="text-base sm:text-lg font-semibold text-purple-700">{selectedMovie}</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedMovie('');
                      setSearchTerm('');
                      setRecommendations([]);
                    }}
                    className="text-purple-600 hover:text-purple-800 transition-colors"
                    aria-label="Clear selection"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                <p className="text-red-700 flex items-center text-sm sm:text-base">
                  <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {error}
                </p>
              </div>
            )}

            {/* Recommend Button */}
            <button
              onClick={getRecommendations}
              disabled={loading || !selectedMovie}
              className={`w-full py-3 sm:py-4 rounded-lg font-bold text-white text-base sm:text-lg transition-all transform hover:scale-105 ${
                loading || !selectedMovie
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Finding Recommendations...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Get Recommendations
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Recommendations Section */}
        {recommendations.length > 0 && (
          <div className="max-w-6xl mx-auto animate-fade-in">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-6 sm:mb-8">
              ✨ You Might Also Like
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {recommendations.map((movie, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:scale-105 hover:shadow-2xl animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="p-6">
                    {/* Rank Badge */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center font-bold text-lg sm:text-xl shadow-lg flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-end">
                          <span className="text-xs sm:text-sm font-semibold text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
                            {movie.similarity_score}% Match
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Movie Title */}
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 min-h-[3rem]">
                      {movie.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-xs sm:text-sm text-gray-600">
                      Based on ML calculations with your selection
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How It Works Section */}
        <div className="max-w-6xl mx-auto mt-12 sm:mt-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-6 sm:mb-8">
            🤔 How Does It Work?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-white/20">
              <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">📊</div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Data Analysis</h3>
              <p className="text-sm sm:text-base text-purple-200">
                Analyzes movie metadata including genres, cast, crew, and keywords from 5000+ movies.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-white/20">
              <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">🧮</div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">ML Algorithm</h3>
              <p className="text-sm sm:text-base text-purple-200">
                Uses cosine similarity to measure how similar movies are based on their features.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-white/20">
              <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">✨</div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Recommendations</h3>
              <p className="text-sm sm:text-base text-purple-200">
                Returns the top 5 most similar movies with match percentages instantly.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white/10 backdrop-blur-md border-t border-white/20 mt-12 sm:mt-16">
        <div className="container mx-auto px-4 py-6 sm:py-8">
          <div className="text-center">
            <p className="text-white font-medium mb-2 text-sm sm:text-base">
              Made with ❤️ by <strong>Vansh Narwani</strong>
            </p>
            <p className="text-purple-200 text-xs sm:text-sm mb-4">
              Powered by Machine Learning & React.js
            </p>
            <div className="flex items-center justify-center space-x-3 sm:space-x-4 text-xs sm:text-sm">
              <a 
                href="https://github.com/Aech0" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-purple-200 hover:text-white transition-colors"
              >
                GitHub
              </a>
              <span className="text-purple-200">•</span>
              <a 
                href="https://linkedin.com/in/vansh-narwani-464b9b217" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-purple-200 hover:text-white transition-colors"
              >
                LinkedIn
              </a>
              <span className="text-purple-200">•</span>
              <a 
                href="https://theoneclickdesigner.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-purple-200 hover:text-white transition-colors"
              >
                Portfolio
              </a>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        
        .animate-slide-up {
          animation: slide-up 0.5s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
}

export default App;